// A ring around every venue with something on inside the trip window, drawn under the
// pins so a listed venue reads as "pin inside a ring". Venues that aren't in a layer
// (most club grounds, most point-to-point fields) get the ring alone; clicking it opens
// what's on there (Map.tsx).
//
// The ring is coloured by feed, so a race meeting and a county final are told apart
// before either is clicked.
import type { CircleLayerSpecification, GeoJSONSource, Map as MLMap } from "maplibre-gl";
import type { EventVenueMarker } from "../state/store";
import { FEEDS, FEED_ORDER } from "../data/events";

export const MATCH_SRC = "event-venues";
export const MATCH_LAYER = "event-rings";
const EMPTY: GeoJSON.FeatureCollection = { type: "FeatureCollection", features: [] };

type Paint = CircleLayerSpecification["paint"];
/** ["match", ["get","feed"], "gaa", <colour>, …, <fallback>] — the ring takes its feed's colour. */
const byFeed = (): NonNullable<Paint>["circle-color"] => {
  const out: unknown[] = ["match", ["get", "feed"]];
  for (const f of FEED_ORDER) out.push(f, FEEDS[f].color);
  out.push(FEEDS.gaa.color);
  return out as NonNullable<Paint>["circle-color"];
};

export function ensureEventRings(map: MLMap): void {
  if (map.getSource(MATCH_SRC)) return;
  map.addSource(MATCH_SRC, { type: "geojson", data: EMPTY });
  map.addLayer({
    id: MATCH_LAYER,
    type: "circle",
    source: MATCH_SRC,
    paint: {
      "circle-radius": ["interpolate", ["linear"], ["zoom"], 5, 5.5, 8, 9, 11, 14, 14, 22],
      "circle-color": byFeed(),
      "circle-opacity": ["interpolate", ["linear"], ["zoom"], 5, 0.1, 9, 0.16],
      "circle-stroke-color": byFeed(),
      "circle-stroke-width": ["interpolate", ["linear"], ["zoom"], 5, 1.2, 11, 2.5],
      "circle-stroke-opacity": ["interpolate", ["linear"], ["zoom"], 5, 0.7, 9, 0.9],
    },
  });
}

export function setEventVenues(map: MLMap, venues: EventVenueMarker[]): void {
  const src = map.getSource(MATCH_SRC) as GeoJSONSource | undefined;
  if (!src) return;
  src.setData({
    type: "FeatureCollection",
    features: venues.map((v) => ({
      type: "Feature",
      geometry: { type: "Point", coordinates: [v.lon, v.lat] },
      properties: { name: v.name, feed: v.feed, place: v.place ?? null, n: v.n },
    })),
  });
}
